import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { keyBy, notification } from '../';
import { create as createStore } from 'zustand';
import { API, BASE_URL } from '../../constants/api';
import { io } from 'socket.io-client';

export const useAppStore = createStore((set, get) => {
  return {
    profile: null,
    establishment: null,

    selectedCompany: null,
    showAllCompanies: false,

    locale: {
      lang: localStorage.getItem('lang') ?? 'es',
      captions: null,
    },

    isDataLoaded: false,
    appMenu: false,
    isMobile: false,
    socket: null,

    setupSocket(profile) {
      const socket = io(`${BASE_URL}${profile.establishment}`);

      socket.on('connect', () => {
        console.log('Escuchando sockets');

        socket.emit('register-user', { _id: profile._id });
      });
      socket.on('error-message', (message) => {
        notification(message, 'warning', 3);
      });

      set({ socket });
    },

    upsert(key, newData) {
      const oldData = get()[key];
      const joinedData = { ...keyBy(oldData, '_id'), ...keyBy(newData, '_id') };
      set({ [key]: Object.values(joinedData) });
    },

    delete(key, ids) {
      const oldData = get()[key] ?? [];
      if (Array.isArray(ids)) {
        set({ [key]: oldData.filter((x) => !ids.includes(x._id)) });
      } else {
        set({ [key]: oldData.filter((x) => x._id !== ids) });
      }
    },

    findById(key, id) {
      const data = get()[key];

      if (!Array.isArray(data)) {
        return null;
      }

      return data.find((item) => item._id === id);
    },

    async getByIdRequest(key, id) {
      key = getType(key);
      const upsert = get().upsert;
      const data = await axios
        .get(`/${key}/${id}`)
        .then((x) => x.data)
        .catch(() => null);

      if (data) {
        upsert(key, data);
      }

      return data;
    },

    async createRequest(key, obj) {
      key = getType(key);
      const upsert = get().upsert;
      const output = await axios
        .post(`/${key}`, obj)
        .then(({ data }) => data)
        .catch(() => null);

      if (output) {
        upsert(key, output);
      }

      return output;
    },

    async updateByIdRequest(key, obj) {
      key = getType(key);
      const upsert = get().upsert;
      const data = await axios
        .put(`/${key}/${obj._id}`, obj)
        .then((x) => x.data)
        .catch(() => null);

      if (data) {
        upsert(key, data);
      }

      return data;
    },

    async updateRequest(key, obj) {
      key = getType(key);
      const data = await axios
        .put(`/${key}`, obj)
        .then((x) => x.data)
        .catch(() => null);

      if (data) {
        set({ [key]: data });
      }

      return data;
    },

    async getAllRequest(key, filter) {
      key = getType(key);
      const { showAllCompanies, upsert } = get();

      const output = await axios
        .get(`/${key}`, {
          params: {
            filter,
            showAll: showAllCompanies,
          },
        })
        .then(({ data }) => {
          if ('data' in data && Array.isArray(data.data)) {
            return data.data;
          }
          return data;
        })
        .catch(() => null);

      if (output) {
        upsert(key, output);
      }

      return output;
    },

    async deleteRequest(key, ids) {
      key = getType(key);
      const storeDelete = get().delete;
      let result = false;
      if (Array.isArray(ids)) {
        result = await axios
          .delete(`/${key}`, { data: ids })
          .then(() => true)
          .catch(() => false);
      } else {
        result = await axios
          .delete(`/${key}/${ids}`)
          .then(() => true)
          .catch(() => false);
      }

      if (result) {
        storeDelete(key, ids);
      }

      return result;
    },

    set,
    get,
  };
});

export const useAppDataSource = (key) => {
  const data = useAppStore((s) => s[key]);
  return useMemo(() => {
    return data ?? [];
  }, [data]);
};

export const useFindById = () => {
  return useAppStore((s) => s.findById);
};

export const useProfile = () => {
  return useAppStore((s) => s[API.PROFILE]);
};

export const useCountries = () => {
  const countries = useAppStore((s) => s[API.COUNTRIES]);
  return useMemo(() => {
    return (countries ?? []).map((x) => ({ value: x.Code, label: x.Name }));
  }, [countries]);
};

const getType = (documentType) => {
  documentType = documentType.split(':').pop();
  return documentType;
};

export const useGetSubTagPrice = () => {
  const findById = useFindById();
  const group = useAppStore((s) => s[API.GROUP]);

  return useCallback(
    (tagId, subTagId) => {
      const tag = findById(API.TAG, tagId);

      if (!tag) {
        return 0;
      }

      const subTag = tag.subTags.find((subTag) => subTag._id === subTagId);

      if (!subTag) {
        return 0;
      }

      const defaultPrice = subTag.unitPrice ?? 0;
      const groupPrice = subTag.groupPrices?.[group?._id];

      return groupPrice ?? defaultPrice;
    },
    [findById, group]
  );
};

export const useCustomFields = (documentType) => {
  const customFields = useAppDataSource(API.CUSTOM_FIELD);
  const customObjects = useAppDataSource(API.CUSTOM_OBJECT);
  const findById = useFindById();

  return useMemo(() => {
    const fields = customFields.filter((x) => x.documentType === documentType);

    const items = fields.map(({ name, description, required, dataType, selectorType }, index) => {
      const dataField = `customFields.${name}`;

      const editorType =
        dataType === 'SELECTOR'
          ? 'dxSelectBox'
          : dataType === 'NUMBER'
          ? 'dxNumberBox'
          : dataType === 'CHECKBOX'
          ? 'dxCheckBox'
          : 'dxTextBox';
      let lookup = undefined;

      if (editorType === 'dxSelectBox') {
        const customObjectType = findById(API.CUSTOM_OBJECT_TYPE, selectorType);
        const displayFields = (customObjectType?.customFields ?? [])
          .filter((x) => x.isDisplayedValue)
          .map((x) => x.name);

        const dataSource = customObjects.filter((x) => x.customObjectType === selectorType);
        const displayedDataSource = dataSource.map((customObject) => ({
          value: customObject._id,
          label: displayFields.map((x) => customObject?.fields?.[x] ?? '').join(' '),
        }));

        lookup = {
          dataSource: displayedDataSource,
          displayExpr: 'label',
          valueExpr: 'value',
        };
      }

      return {
        dataField,
        name: dataField,
        label: { text: description ?? `Campo personalizado ${index + 1}` },
        editorType,
        isRequired: required,
        editorOptions: {
          ...lookup,
        },
      };
    });

    return {
      itemType: 'group',
      items,
    };
  }, [documentType, customFields, findById, customObjects]);
};
